import React, { useCallback, useMemo, useState, useRef, useLayoutEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { MdDashboard } from "react-icons/md";
import Header from "../../business/header/Header";
import SidebarUnified from "./SidebarUnified";
import useResponsiveSidebar from "../../business/hooks/useResponsiveSidebar";
import "./AppShell.css"

export default function AppShell({
  basePath = "/home",
  tabs = [],
  routes = [],
  isAdmin = false,
  titleResolver,
  getActivePath,
  indexRedirect,
  onTabNavigate,
}) {
  const [menuOpen, setMenuOpen] = useResponsiveSidebar("(min-width: 992px)");
  const location = useLocation();
  const navigate = useNavigate();
  const [scrollContainer, setScrollContainer] = useState(null);
  const containerRef = useRef(null);
  const bindScrollContainer = useCallback((node) => {
    setScrollContainer(node || null);
  }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof window === "undefined") return undefined;

    const updateOffset = () => {
      const rect = el.getBoundingClientRect();
      const top = rect?.top ?? 0;
      el.style.setProperty("--app-shell-top-offset", `${Math.max(0, top)}px`);
    };

    updateOffset();
    const handleResize = () => updateOffset();
    window.addEventListener("resize", handleResize);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateOffset);
      ro.observe(document.body);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (ro) ro.disconnect();
    };
  }, []);

  const currentTitle = useMemo(
    () => (titleResolver ? titleResolver(location.pathname) : "Dashboard"),
    [location.pathname, titleResolver]
  );

  const RequireAdmin = ({ children }) => (isAdmin ? children : <Navigate to={`${basePath}/transactions`} replace />);

  return (
    <div className="main-container d-flex flex-column" ref={containerRef}>
      <Header
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        basePath={basePath}
        scrollContainer={scrollContainer}
      />

      <div className="layout-body d-flex flex-grow-1">
        <SidebarUnified
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          tabs={tabs}
          basePath={basePath}
          getActivePath={getActivePath}
          onTabNavigate={(tab, navigateDefault) => {
            if (onTabNavigate) return onTabNavigate(tab, navigateDefault, { navigate });
            navigateDefault(tab);
          }}
        />

        <div
          ref={bindScrollContainer}
          className="content-wrapper flex-grow-1 p-2 d-flex justify-content-center bg-body"
        >
          <main className="content mb-4 w-100" role="main" aria-label={currentTitle}>
            <div className="dashboard-header">
              <h2 className="dashboard-title d-inline-flex align-items-center gap-2">
                <MdDashboard className="dashboard-icon" />
                {currentTitle}
              </h2>
            </div>

            <Routes>
              {indexRedirect && <Route index element={<Navigate to={indexRedirect} replace />} />}

              {routes.map((r) => {
                const element = r.adminOnly
                  ? <RequireAdmin>{r.element}</RequireAdmin>
                  : r.element;

                return r.path === "" ? (
                  <Route key={r.key} index element={element} />
                ) : (
                  <Route key={r.key} path={r.path} element={element} />
                );
              })}
            </Routes>
          </main>
        </div>
      </div>

    </div>
  );
}
